import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Step,
  StepButton,
  StepContent,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  DeleteOutline as ExcludeIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Inventory2 as InventoryIcon,
  KeyboardArrowDown as DownIcon,
  KeyboardArrowRight as RightIcon,
  LocalShipping as TruckIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
  ViewList as ListIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';
import {
  FboTruckCalendar,
  FboTruckDetailsDialog,
  FboTruckList,
  normalizeFboTruckPlan,
} from '../../components/analytics/fbo';
import axios from '../../utils/axios';
import { notify } from '../../utils/notify';
import { useIsMobile } from '../../utils/responsive';
import {
  MAX_SUPPLY_HORIZON_DAYS,
  MIN_SUPPLY_HORIZON_DAYS,
  resolveSupplyPlanDefaults,
} from './supplyPlanDefaults.mjs';

const MODE_PLAN = 'plan';
const MODE_AVERAGE_SALES = 'average_sales';
const VIEW_ITEMS = 'items';
const VIEW_TRUCK_CALENDAR = 'truck-calendar';
const VIEW_TRUCK_LIST = 'truck-list';
const DEFAULT_ASSORTMENT_FILTERS = { mp: '1' };
const MAX_EXCLUDED_PRODUCTS = 500;
const STEPS = ['Метод', 'Параметры', 'Ассортимент', 'Результат'];
const TARGET_SOURCE_LABEL = {
  sku: 'для этого баркода',
  article: 'для артикула',
  podgruppa: 'для подгруппы',
  gruppa: 'для группы',
  global: 'общее значение',
  default: 'значение по умолчанию',
};

const fmtNum = (value) => (value == null
  ? '—'
  : new Intl.NumberFormat('ru-RU').format(Math.round(Number(value))));
const fmtFloat = (value) => (value == null
  ? '—'
  : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(Number(value)));
const fmtDate = (value) => (value
  ? new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU')
  : '—');
const productKeyOf = (item) => String(item?.product_key || item?.nm_id || '').trim();
const toExcludedProduct = (item) => {
  const productKey = productKeyOf(item);
  return {
    product_key: productKey,
    nm_id: item.nm_id || productKey,
    article: item.article || `nmID ${productKey}`,
    image_url: item.image_url || null,
    organization: item.organization || null,
  };
};
const mergeProductExclusions = (current, additions) => {
  const byKey = new Map(current.map((item) => [productKeyOf(item), item]).filter(([key]) => key));
  additions.forEach((item) => {
    const key = productKeyOf(item);
    if (key) byKey.set(key, toExcludedProduct(item));
  });
  return [...byKey.values()];
};

const countAssortmentFilters = (filters = {}) => {
  let count = 0;
  if ((filters.orgMode === 'include' && filters.orgSel?.length)
    || (filters.orgMode === 'exclude' && filters.orgExcluded?.length)) count += 1;
  if ((filters.gruppaMode === 'include' && filters.gruppa?.length)
    || (filters.gruppaMode === 'exclude' && filters.gruppaExcluded?.length)) count += 1;
  if ((filters.podgruppaMode === 'include' && filters.podgruppa?.length)
    || (filters.podgruppaMode === 'exclude' && filters.podgruppaExcluded?.length)) count += 1;
  if (filters.selectedArticle?.article) count += 1;
  return count;
};

const renderSchedule = (schedule = []) => {
  if (!schedule.length) return '—';
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      {schedule.slice(0, 4).map((entry, index) => (
        <Tooltip
          key={`${entry.ship_date}_${entry.arrival_date}_${index}`}
          title={[
            `Целевой запас: ${fmtNum(entry.target_stock)} шт.`,
            entry.units_per_pallet != null ? `В палете: ${fmtNum(entry.units_per_pallet)} шт.` : '',
            entry.pallets != null ? `Палет: ${fmtFloat(entry.pallets)}.` : '',
            entry.pallet_rounding_applied ? 'Количество округлено до целой палеты.' : '',
          ].filter(Boolean).join(' ')}
        >
          <Chip
            size="small"
            color={entry.min_qty_applied || entry.pallet_rounding_applied ? 'warning' : 'default'}
            variant="outlined"
            label={`${fmtDate(entry.ship_date)} → ${fmtDate(entry.arrival_date)} · ${fmtNum(entry.qty)} шт.`}
          />
        </Tooltip>
      ))}
      {schedule.length > 4 && (
        <Typography variant="caption" color="text.secondary">
          Ещё поставок: {schedule.length - 4}
        </Typography>
      )}
    </Stack>
  );
};

function StepSection({ title, description, children }) {
  return (
    <Box sx={{ pt: 1, pb: 2 }}>
      {title && <Typography variant="titleMedium" fontWeight={700}>{title}</Typography>}
      {description && (
        <Typography variant="bodyMedium" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          {description}
        </Typography>
      )}
      {children}
    </Box>
  );
}

export default function SupplyPlan() {
  const isMobile = useIsMobile();
  const [activeStep, setActiveStep] = useState(0);
  const [plans, setPlans] = useState([]);
  const [calculationMode, setCalculationMode] = useState(MODE_PLAN);
  const [planId, setPlanId] = useState('');
  const [horizonDays, setHorizonDays] = useState('');
  const [shareDays, setShareDays] = useState(60);
  const [rateDays, setRateDays] = useState(30);
  const [leadDays, setLeadDays] = useState('');
  const [minSupplyQty, setMinSupplyQty] = useState('');
  const [globalTarget, setGlobalTarget] = useState('');
  const [savedGlobalTarget, setSavedGlobalTarget] = useState('');
  const [globalTargetSaving, setGlobalTargetSaving] = useState(false);
  const [defaultsSaving, setDefaultsSaving] = useState(false);
  const [defaultsSaveStatus, setDefaultsSaveStatus] = useState('idle');
  const [defaultsSaveMessage, setDefaultsSaveMessage] = useState('');
  const [federalDistricts, setFederalDistricts] = useState([]);
  const [districtShort, setDistrictShort] = useState({});
  const [selectedOkrugs, setSelectedOkrugs] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(true);
  const [districtsError, setDistrictsError] = useState('');
  const [assortmentFilters, setAssortmentFilters] = useState(DEFAULT_ASSORTMENT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [editKey, setEditKey] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [viewMode, setViewMode] = useState(VIEW_ITEMS);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSavedPlan, setLastSavedPlan] = useState(null);
  const [excludedProducts, setExcludedProducts] = useState([]);
  const [excludedListOpen, setExcludedListOpen] = useState(false);
  const [selectedProductKeys, setSelectedProductKeys] = useState([]);
  const parametersTouchedRef = useRef(false);
  const globalTargetTouchedRef = useRef(false);

  useEffect(() => {
    axios.get('/analytics/supply-plan/plans')
      .then((response) => {
        const nextPlans = response.data || [];
        setPlans(nextPlans);
        if (nextPlans.length) setPlanId((current) => current || nextPlans[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get('/analytics/supply-plan/targets', { params: { scope: 'global', marketplace: 1 } })
      .then((response) => {
        const row = (response.data || []).find((target) => target.scope === 'global');
        if (!row) return;
        if (!globalTargetTouchedRef.current) setGlobalTarget(String(row.target_days));
        setSavedGlobalTarget(String(row.target_days));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      axios.get('/analytics/supply-plan/defaults'),
      axios.get('/analytics/supply-plan/warehouse-regions'),
    ]).then(([defaultsResult, regionsResult]) => {
      if (!active) return;
      if (regionsResult.status === 'fulfilled') {
        const responseData = regionsResult.value.data;
        const districts = Array.isArray(responseData?.federal_districts)
          ? responseData.federal_districts
          : [];
        setFederalDistricts(districts);
        setDistrictShort(responseData?.district_short || {});
        if (!parametersTouchedRef.current) {
          const resolved = resolveSupplyPlanDefaults(
            defaultsResult.status === 'fulfilled' ? defaultsResult.value.data : null,
            districts,
          );
          if (resolved.horizonDays != null) setHorizonDays(String(resolved.horizonDays));
          setSelectedOkrugs(resolved.okrug);
        }
        setDistrictsError(districts.length ? '' : 'Справочник федеральных округов пуст.');
      } else {
        setDistrictsError('Не удалось загрузить справочник федеральных округов.');
      }
      setDistrictsLoading(false);
    });
    return () => { active = false; };
  }, []);

  const invalidateResult = useCallback(() => {
    setData(null);
    setError('');
    setSelectedTruck(null);
    setLastSavedPlan(null);
  }, []);

  const assortmentFilterCount = useMemo(
    () => countAssortmentFilters(assortmentFilters),
    [assortmentFilters],
  );

  const buildSupplyParams = useCallback((excludedOverride) => {
    const exclusions = Array.isArray(excludedOverride) ? excludedOverride : excludedProducts;
    const params = {
      mode: calculationMode,
      share_days: shareDays,
      rate_days: rateDays,
      ...buildFilterParams({ ...assortmentFilters, mp: '1' }),
    };
    if (calculationMode === MODE_PLAN) params.plan_id = planId;
    if (horizonDays) params.horizon_days = horizonDays;
    if (leadDays) params.lead_days = leadDays;
    if (Number(minSupplyQty) > 0) params.min_supply_qty = minSupplyQty;
    if (selectedOkrugs.length) params.okrug = selectedOkrugs.join(',');
    const excludedKeys = [...new Set(
      exclusions.map((item) => String(item.product_key || '').trim()).filter(Boolean),
    )];
    if (excludedKeys.length) params.exclude_product_keys = excludedKeys.join(',');
    return params;
  }, [
    assortmentFilters,
    calculationMode,
    horizonDays,
    leadDays,
    minSupplyQty,
    planId,
    rateDays,
    selectedOkrugs,
    shareDays,
    excludedProducts,
  ]);

  const persistGlobalTarget = useCallback(async (targetDays, silent = false) => {
    setGlobalTargetSaving(true);
    try {
      await axios.post('/analytics/supply-plan/targets', {
        scope: 'global',
        entity_key: '',
        marketplace: 1,
        target_days: targetDays,
      });
      setSavedGlobalTarget(String(targetDays));
      if (!silent) notify('Целевой запас сохранён', { variant: 'success' });
      return true;
    } catch (_) {
      return false;
    } finally {
      setGlobalTargetSaving(false);
    }
  }, []);

  const ensureGlobalTargetSaved = useCallback(async () => {
    if (globalTarget === '') return true;
    const targetDays = Number(globalTarget);
    if (!Number.isFinite(targetDays) || targetDays <= 0) {
      setError('Целевой запас должен быть положительным числом дней.');
      return false;
    }
    if (savedGlobalTarget !== '' && Number(savedGlobalTarget) === targetDays) return true;
    return persistGlobalTarget(targetDays, true);
  }, [globalTarget, persistGlobalTarget, savedGlobalTarget]);

  const compute = useCallback(async (options = {}) => {
    const requestedExclusions = Array.isArray(options.excludedProducts)
      ? options.excludedProducts
      : excludedProducts;
    setActiveStep(3);
    setError('');
    if (calculationMode === MODE_PLAN && !planId) {
      setError('Выберите план продаж на первом шаге.');
      return;
    }
    if (!selectedOkrugs.length) {
      setError('Выберите хотя бы один федеральный округ на шаге «Параметры».');
      return;
    }
    if (!(await ensureGlobalTargetSaved())) return;
    setLoading(true);
    if (!options.preserveDataOnError) setData(null);
    setSelectedTruck(null);
    try {
      const response = await axios.get('/analytics/supply-plan/preview', {
        params: buildSupplyParams(requestedExclusions),
      });
      setData(response.data);
      const appliedByKey = new Map(
        (response.data?.excluded_items || []).map((item) => [String(item.product_key), item]),
      );
      setExcludedProducts(requestedExclusions
        .filter((item) => appliedByKey.has(String(item.product_key)))
        .map((item) => ({ ...item, ...appliedByKey.get(String(item.product_key)) })));
      const activeKeys = new Set((response.data?.items || []).map(productKeyOf).filter(Boolean));
      setSelectedProductKeys((current) => current.filter((key) => activeKeys.has(key)));
      setExpanded({});
      setShowAllWarnings(false);
      return response.data;
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message
          || 'Не удалось выполнить расчёт. Проверьте параметры и повторите попытку.',
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [
    buildSupplyParams,
    calculationMode,
    ensureGlobalTargetSaved,
    excludedProducts,
    planId,
    selectedOkrugs,
  ]);

  const markParametersEdited = () => {
    parametersTouchedRef.current = true;
    setDefaultsSaveStatus('idle');
    setDefaultsSaveMessage('');
  };

  const saveGlobalTarget = async () => {
    const targetDays = Number(globalTarget);
    if (!Number.isFinite(targetDays) || targetDays <= 0) {
      setError('Целевой запас должен быть положительным числом дней.');
      return;
    }
    const horizon = Number(horizonDays);
    if (
      !Number.isInteger(horizon)
      || horizon < MIN_SUPPLY_HORIZON_DAYS
      || horizon > MAX_SUPPLY_HORIZON_DAYS
    ) {
      setError(
        `Горизонт планирования должен быть целым числом от ${MIN_SUPPLY_HORIZON_DAYS} `
        + `до ${MAX_SUPPLY_HORIZON_DAYS} дней.`,
      );
      return;
    }
    if (!selectedOkrugs.length) {
      setError('Выберите хотя бы один федеральный округ.');
      return;
    }
    setError('');
    setDefaultsSaving(true);
    setDefaultsSaveStatus('saving');
    setDefaultsSaveMessage('');
    const [targetResult, defaultsResult] = await Promise.allSettled([
      axios.post('/analytics/supply-plan/targets', {
        scope: 'global',
        entity_key: '',
        marketplace: 1,
        target_days: targetDays,
      }),
      axios.put('/analytics/supply-plan/defaults', {
        horizon_days: horizon,
        okrug: selectedOkrugs,
      }),
    ]);
    const targetSaved = targetResult.status === 'fulfilled';
    const defaultsSaved = defaultsResult.status === 'fulfilled';
    if (targetSaved) setSavedGlobalTarget(String(targetDays));

    if (targetSaved && defaultsSaved) {
      setDefaultsSaveStatus('success');
      setDefaultsSaveMessage('');
      notify('Настройки будущих расчётов сохранены', { variant: 'success' });
      invalidateResult();
    } else {
      const message = targetSaved
        ? 'Целевой запас сохранён, но горизонт и округа сохранить не удалось.'
        : defaultsSaved
          ? 'Горизонт и округа сохранены, но целевой запас сохранить не удалось.'
          : 'Не удалось сохранить целевой запас, горизонт и округа.';
      setDefaultsSaveStatus('error');
      setDefaultsSaveMessage(message);
      notify(message, { variant: 'error' });
    }
    setDefaultsSaving(false);
  };

  const saveCurrentAsPlan = async () => {
    if (!selectedOkrugs.length) {
      notify('Выберите хотя бы один федеральный округ', { variant: 'warning' });
      return;
    }
    const name = saveName.trim();
    if (!name) {
      notify('Введите название плана поставок', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post('/analytics/supply-plan/saved-plans/from-preview', {
        name,
        settings: buildSupplyParams(),
        filters: { ...assortmentFilters, mp: '1' },
      });
      setLastSavedPlan(response.data);
      setSaveDialogOpen(false);
      setSaveName('');
      notify('План поставок сохранён', { variant: 'success' });
    } finally {
      setSaving(false);
    }
  };

  const downloadExcel = async () => {
    if (calculationMode === MODE_PLAN && !planId) return;
    if (!selectedOkrugs.length) {
      setError('Выберите хотя бы один федеральный округ на шаге «Параметры».');
      return;
    }
    if (!(await ensureGlobalTargetSaved())) return;
    const query = new URLSearchParams();
    Object.entries(buildSupplyParams()).forEach(([key, value]) => {
      if (value != null && value !== '') query.set(key, String(value));
    });
    try {
      const token = localStorage.getItem('token');
      const base = axios.defaults.baseURL || '/api';
      const response = await fetch(
        `${base}/analytics/supply-plan/export?${query.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error('Ошибка выгрузки');
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Поставки_FBO_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (_) {
      notify('Не удалось скачать файл', { variant: 'error' });
    }
  };

  const items = data?.items || [];
  const shortMap = data?.region_short || {};
  const warnings = data?.warnings || [];
  const visibleWarnings = showAllWarnings ? warnings : warnings.slice(0, 3);
  const truckPlan = useMemo(
    () => normalizeFboTruckPlan(data?.truck_plan || data?.truckPlan),
    [data],
  );
  const totals = useMemo(
    () => items.reduce((result, item) => ({
      toSupply: result.toSupply + Number(item.total_to_supply || 0),
      scheduleTotal: result.scheduleTotal + Number(item.schedule_total_qty || 0),
      stock: result.stock + Number(item.total_stock || 0),
    }), { toSupply: 0, scheduleTotal: 0, stock: 0 }),
    [items],
  );
  const selectableItemsByKey = useMemo(() => {
    const byKey = new Map();
    items.forEach((item) => {
      const key = productKeyOf(item);
      if (key && !byKey.has(key)) byKey.set(key, item);
    });
    return byKey;
  }, [items]);
  const selectableProductKeys = useMemo(
    () => [...selectableItemsByKey.keys()],
    [selectableItemsByKey],
  );
  const selectedVisibleKeys = useMemo(() => {
    const visible = new Set(selectableProductKeys);
    return selectedProductKeys.filter((key) => visible.has(key));
  }, [selectableProductKeys, selectedProductKeys]);
  const allVisibleSelected = selectableProductKeys.length > 0
    && selectedVisibleKeys.length === selectableProductKeys.length;

  const excludeProduct = (item) => {
    const productKey = productKeyOf(item);
    if (!productKey) {
      notify('У товара нет стабильного ключа WB', { variant: 'warning' });
      return;
    }
    const next = mergeProductExclusions(excludedProducts, [item]);
    if (next.length > MAX_EXCLUDED_PRODUCTS) {
      notify(`Можно исключить не более ${MAX_EXCLUDED_PRODUCTS} товаров`, { variant: 'warning' });
      return;
    }
    setExcludedProducts(next);
    notify(`Товар «${item.article || productKey}» исключён из поставки`, { variant: 'info' });
    compute({ excludedProducts: next });
  };

  const toggleProductSelection = (productKey) => {
    setSelectedProductKeys((current) => (
      current.includes(productKey)
        ? current.filter((key) => key !== productKey)
        : [...current, productKey]
    ));
  };

  const toggleAllVisibleProducts = () => {
    setSelectedProductKeys((current) => {
      const visible = new Set(selectableProductKeys);
      if (allVisibleSelected) return current.filter((key) => !visible.has(key));
      return [...new Set([...current, ...selectableProductKeys])];
    });
  };

  const excludeSelectedProducts = async () => {
    if (!selectedVisibleKeys.length) return;
    const selectedItems = selectedVisibleKeys
      .map((key) => selectableItemsByKey.get(key))
      .filter(Boolean);
    const next = mergeProductExclusions(excludedProducts, selectedItems);
    if (next.length > MAX_EXCLUDED_PRODUCTS) {
      notify(
        `Нельзя исключить больше ${MAX_EXCLUDED_PRODUCTS} товаров. Верните часть исключённых позиций и повторите.`,
        { variant: 'warning' },
      );
      return;
    }
    const result = await compute({ excludedProducts: next, preserveDataOnError: true });
    if (!result) {
      notify('Не удалось исключить выбранные товары. Выбор сохранён — повторите попытку.', {
        variant: 'error',
      });
      return;
    }
    setSelectedProductKeys([]);
    notify(`Исключено из поставки: ${selectedItems.length}`, { variant: 'success' });
  };

  const restoreProduct = (productKey) => {
    const next = excludedProducts.filter((item) => item.product_key !== String(productKey));
    setExcludedProducts(next);
    compute({ excludedProducts: next });
  };

  const restoreAllProducts = () => {
    setExcludedProducts([]);
    setExcludedListOpen(false);
    compute({ excludedProducts: [] });
  };

  const saveTarget = async (item) => {
    const targetDays = Number(editVal);
    if (!Number.isFinite(targetDays) || targetDays <= 0) {
      notify('Введите положительное число дней', { variant: 'warning' });
      return;
    }
    if (!item.barcode) return;
    await axios.post('/analytics/supply-plan/targets', {
      scope: 'sku',
      entity_key: item.barcode,
      marketplace: 1,
      target_days: targetDays,
    });
    setEditKey(null);
    setEditVal('');
    notify('Целевой запас товара сохранён', { variant: 'success' });
    compute();
  };

  const methodContent = (
    <StepSection
      title="Выберите основу расчёта"
      description="Можно использовать утверждённый план продаж или фактическую скорость продаж за выбранный период."
    >
      <Stack spacing={2}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          orientation={isMobile ? 'vertical' : 'horizontal'}
          value={calculationMode}
          onChange={(_, value) => {
            if (!value) return;
            setCalculationMode(value);
            invalidateResult();
          }}
        >
          <ToggleButton value={MODE_PLAN}>По плану продаж</ToggleButton>
          <ToggleButton value={MODE_AVERAGE_SALES}>По средним продажам</ToggleButton>
        </ToggleButtonGroup>
        {calculationMode === MODE_PLAN ? (
          <FormControl fullWidth size="small">
            <InputLabel>План продаж</InputLabel>
            <Select
              label="План продаж"
              value={planId}
              onChange={(event) => {
                setPlanId(event.target.value);
                invalidateResult();
              }}
            >
              {plans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Alert severity="info">
            Спрос будет рассчитан по фактическим продажам WB за указанное окно.
          </Alert>
        )}
      </Stack>
    </StepSection>
  );

  const parametersContent = (
    <StepSection
      title="Настройте расчёт"
      description="Пустые необязательные поля используют серверные значения по умолчанию."
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Горизонт планирования, дней"
            value={horizonDays}
            onChange={(event) => {
              markParametersEdited();
              setHorizonDays(event.target.value);
              invalidateResult();
            }}
            placeholder={calculationMode === MODE_AVERAGE_SALES ? '30' : 'Срок плана продаж'}
            helperText="На сколько дней вперёд построить график."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Окно продаж, дней"
            value={rateDays}
            onChange={(event) => { setRateDays(event.target.value); invalidateResult(); }}
            helperText="Период для оценки текущей скорости продаж."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Tooltip title="Используется только с планом продаж: показывает, за какой период оценивать долю спроса каждого региона.">
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Распределение по регионам, дней"
              value={shareDays}
              disabled={calculationMode === MODE_AVERAGE_SALES}
              onChange={(event) => { setShareDays(event.target.value); invalidateResult(); }}
              helperText="История для распределения спроса между округами."
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Целевой запас, дней"
            value={globalTarget}
            onChange={(event) => {
              globalTargetTouchedRef.current = true;
              setDefaultsSaveStatus('idle');
              setDefaultsSaveMessage('');
              setGlobalTarget(event.target.value);
            }}
            placeholder="30"
            helperText="Общее значение для товаров без индивидуальной настройки."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Минимальная партия, шт."
            value={minSupplyQty}
            onChange={(event) => { setMinSupplyQty(event.target.value); invalidateResult(); }}
            placeholder="1"
            helperText="Меньшая рекомендация будет округлена вверх."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Срок до склада WB, дней"
            value={leadDays}
            onChange={(event) => { setLeadDays(event.target.value); invalidateResult(); }}
            placeholder="Автоматически по округу"
            helperText="Включает доставку и приёмку на складе WB."
          />
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={1}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              fullWidth
              size="small"
              loading={districtsLoading}
              options={federalDistricts}
              value={selectedOkrugs}
              getOptionLabel={(okrug) => `${districtShort[okrug] || okrug} — ${okrug}`}
              onChange={(_, value) => {
                markParametersEdited();
                setSelectedOkrugs(value);
                invalidateResult();
              }}
              renderOption={(props, okrug, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  <Tooltip title={okrug} placement="right">
                    <Stack>
                      <Typography variant="bodyMedium" fontWeight={600}>
                        {districtShort[okrug] || okrug}
                      </Typography>
                      <Typography variant="bodySmall" color="text.secondary">
                        {okrug}
                      </Typography>
                    </Stack>
                  </Tooltip>
                </li>
              )}
              renderTags={(value) => (
                <Chip
                  size="small"
                  label={`Выбрано округов: ${value.length} из ${federalDistricts.length}`}
                />
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Федеральные округа"
                  error={!districtsLoading && (!selectedOkrugs.length || Boolean(districtsError))}
                  helperText={districtsError || (selectedOkrugs.length
                    ? 'Результаты, Excel и план фур будут построены только по выбранным округам.'
                    : 'Для расчёта выберите хотя бы один округ.')}
                />
              )}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                onClick={() => {
                  markParametersEdited();
                  setSelectedOkrugs(federalDistricts);
                  invalidateResult();
                }}
                disabled={districtsLoading || selectedOkrugs.length === federalDistricts.length}
              >
                Выбрать все
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  markParametersEdited();
                  setSelectedOkrugs([]);
                  invalidateResult();
                }}
                disabled={districtsLoading || !selectedOkrugs.length}
              >
                Очистить
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <FormControl>
            <Tooltip title="Сохранит целевой запас, горизонт и округа для будущих расчётов">
              <span>
                <Button
                  variant="outlined"
                  color={defaultsSaveStatus === 'error' ? 'error' : 'primary'}
                  startIcon={defaultsSaveStatus === 'success' ? <CheckIcon /> : <SaveIcon />}
                  onClick={saveGlobalTarget}
                  disabled={globalTargetSaving || defaultsSaving || districtsLoading}
                >
                  {defaultsSaving
                    ? 'Сохраняем…'
                    : defaultsSaveStatus === 'success'
                      ? 'Настройки сохранены'
                      : defaultsSaveStatus === 'error'
                        ? 'Повторить сохранение'
                        : 'Сохранить как значение по умолчанию'}
                </Button>
              </span>
            </Tooltip>
            <FormHelperText
              error={defaultsSaveStatus === 'error'}
              sx={{ ml: 0, maxWidth: 520 }}
            >
              {defaultsSaveMessage
                || 'Сохранит целевой запас, горизонт и округа для будущих расчётов.'}
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </StepSection>
  );

  const assortmentContent = (
    <StepSection
      title="Ограничьте ассортимент"
      description="Расчёт выполняется только для Wildberries. Без фильтров будут включены все доступные WB-товары."
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            color={assortmentFilterCount ? 'primary' : 'default'}
            variant="outlined"
            label={assortmentFilterCount
              ? `Выбрано условий: ${assortmentFilterCount}`
              : 'Все товары Wildberries'}
          />
          {assortmentFilterCount > 0 && (
            <Button
              size="small"
              onClick={() => {
                setAssortmentFilters(DEFAULT_ASSORTMENT_FILTERS);
                invalidateResult();
              }}
            >
              Сбросить фильтры
            </Button>
          )}
        </Stack>
        <ProductFilters
          showMp={false}
          defaultMp="1"
          value={{ ...assortmentFilters, mp: '1' }}
          onChange={(next) => {
            setAssortmentFilters({ ...next, mp: '1' });
            invalidateResult();
          }}
        />
      </Stack>
    </StepSection>
  );

  const resultIntro = (
    <StepSection>
      {!loading && !data && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, textAlign: 'center' }}>
          <CalculateIcon color="primary" sx={{ fontSize: 44, mb: 1 }} />
          <Typography variant="titleMedium" fontWeight={700}>Всё готово к расчёту</Typography>
          <Typography variant="bodyMedium" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Проверьте выбранный метод, параметры и ассортимент. К шагам можно вернуться без потери настроек.
          </Typography>
          <Button variant="contained" startIcon={<CalculateIcon />} onClick={compute}>
            Рассчитать поставки
          </Button>
        </Paper>
      )}
    </StepSection>
  );

  return (
    <Box>
      <PageHeader
        title="Планирование поставок FBO"
        subtitle="Рекомендации по товарам, регионам и фурам для Wildberries"
      />

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stepper
          nonLinear
          activeStep={activeStep}
          orientation={isMobile ? 'vertical' : 'horizontal'}
        >
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < activeStep && (index !== 3 || Boolean(data))}>
              <StepButton color="inherit" onClick={() => setActiveStep(index)}>
                <StepLabel optional={!isMobile && index === 3 && data ? 'Расчёт готов' : undefined}>
                  {label}
                </StepLabel>
              </StepButton>
              {isMobile && (
                <StepContent>
                  {index === 0 && methodContent}
                  {index === 1 && parametersContent}
                  {index === 2 && assortmentContent}
                  {index === 3 && resultIntro}
                  <Stack direction="row" spacing={1}>
                    {index < 3 && (
                      <Button variant="contained" onClick={() => setActiveStep(index + 1)}>
                        Далее
                      </Button>
                    )}
                    {index > 0 && (
                      <Button onClick={() => setActiveStep(index - 1)}>Назад</Button>
                    )}
                  </Stack>
                </StepContent>
              )}
            </Step>
          ))}
        </Stepper>
        {!isMobile && (
          <Box sx={{ mt: 3 }}>
            {activeStep === 0 && methodContent}
            {activeStep === 1 && parametersContent}
            {activeStep === 2 && assortmentContent}
            {activeStep === 3 && resultIntro}
            {activeStep < 3 && (
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {activeStep > 0 && (
                  <Button onClick={() => setActiveStep((step) => step - 1)}>Назад</Button>
                )}
                <Button variant="contained" onClick={() => setActiveStep((step) => step + 1)}>
                  Далее
                </Button>
              </Stack>
            )}
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={(
          <Button color="inherit" size="small" onClick={compute}>Повторить</Button>
        )}>
          <AlertTitle>Расчёт не выполнен</AlertTitle>
          {error}
        </Alert>
      )}

      {loading && (
        <Stack spacing={2} aria-label="Выполняется расчёт">
          <Alert severity="info">Собираем продажи, остатки и формируем график поставок…</Alert>
          <Grid container spacing={2}>
            {[0, 1, 2, 3].map((key) => (
              <Grid item xs={12} sm={6} lg={3} key={key}>
                <Skeleton variant="rounded" height={170} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rounded" height={260} />
        </Stack>
      )}

      {!loading && data && (
        <Stack spacing={3}>
          {excludedProducts.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Typography variant="bodyMedium" fontWeight={700}>
                    Исключено товаров: {excludedProducts.length}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    onClick={() => setExcludedListOpen((open) => !open)}
                  >
                    {excludedListOpen ? 'Скрыть список' : 'Открыть список'}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<RestoreIcon />}
                    onClick={restoreAllProducts}
                  >
                    Вернуть все
                  </Button>
                </Stack>
                <Collapse in={excludedListOpen}>
                  <Stack spacing={0.5} sx={{ pt: 0.5 }}>
                    {excludedProducts.map((item) => (
                      <Stack
                        key={item.product_key}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ py: 0.5 }}
                      >
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography variant="bodySmall" fontWeight={600} noWrap>
                            {item.article || `nmID ${item.product_key}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            nmID {item.product_key}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<RestoreIcon />}
                          onClick={() => restoreProduct(item.product_key)}
                        >
                          Вернуть
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
            </Paper>
          )}

          {warnings.length > 0 && (
            <Alert severity="warning">
              <AlertTitle>Требуют внимания: {warnings.length}</AlertTitle>
              <Box component="ul" sx={{ my: 0, pl: 2 }}>
                {visibleWarnings.map((warning, index) => (
                  <li key={`${warning}_${index}`}>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                ))}
              </Box>
              {warnings.length > 3 && (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setShowAllWarnings((value) => !value)}
                  sx={{ mt: 1 }}
                >
                  {showAllWarnings ? 'Свернуть' : `Показать все (${warnings.length})`}
                </Button>
              )}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="К поставке сейчас"
                value={`${fmtNum(totals.toSupply)} шт.`}
                subtitle="По текущему дефициту"
                color="primary"
                icon={<InventoryIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="По графику"
                value={`${fmtNum(totals.scheduleTotal)} шт.`}
                subtitle={`Горизонт: ${data.horizon_days || '—'} дней`}
                color="secondary"
                icon={<TruckIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Текущий остаток"
                value={`${fmtNum(totals.stock)} шт.`}
                subtitle={data.stock_dt ? `На ${new Date(data.stock_dt).toLocaleString('ru-RU')}` : 'Последние доступные данные'}
                color="info"
                icon={<WarehouseIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard
                title="Товарных позиций"
                value={fmtNum(items.length)}
                subtitle={`Фур в расчёте: ${fmtNum(truckPlan.trucks.length)}`}
                color="success"
                icon={<CalculateIcon />}
              />
            </Grid>
          </Grid>

          <Paper sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <ToggleButtonGroup
                exclusive
                fullWidth={isMobile}
                size="small"
                value={viewMode}
                onChange={(_, value) => { if (value) setViewMode(value); }}
              >
                <ToggleButton value={VIEW_ITEMS}>
                  <InventoryIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Товары
                </ToggleButton>
                <ToggleButton value={VIEW_TRUCK_CALENDAR}>
                  <TruckIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Календарь фур
                </ToggleButton>
                <ToggleButton value={VIEW_TRUCK_LIST}>
                  <ListIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Список фур
                </ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadExcel}>
                Скачать Excel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={!truckPlan.trucks.length || saving}
                onClick={() => {
                  setSaveName(`План поставок ${new Date().toLocaleDateString('ru-RU')}`);
                  setSaveDialogOpen(true);
                }}
              >
                Сохранить план
              </Button>
              <Button component={RouterLink} to="/analytics/fbo-supply-plans" variant="text">
                Сохранённые планы
              </Button>
            </Stack>
            {lastSavedPlan && (
              <Alert severity="success" sx={{ mt: 2 }} action={(
                <Button
                  component={RouterLink}
                  to={`/analytics/fbo-supply-plans?id=${lastSavedPlan.id}`}
                  color="inherit"
                  size="small"
                >
                  Открыть
                </Button>
              )}>
                План «{lastSavedPlan.name}» сохранён.
              </Alert>
            )}
          </Paper>

          {!items.length && viewMode === VIEW_ITEMS ? (
            <Alert severity="info">
              <AlertTitle>Нет товаров для поставки</AlertTitle>
              По выбранным параметрам и фильтрам рекомендации не сформированы. Проверьте ассортимент,
              период продаж или увеличьте горизонт планирования.
            </Alert>
          ) : viewMode === VIEW_TRUCK_CALENDAR ? (
            <>
              <FboTruckCalendar
                truckPlan={truckPlan}
                readOnly
                editable={false}
                dndEnabled={false}
                onTruckOpen={(_, truck) => setSelectedTruck(truck)}
                emptyMessage="Фуры не сформированы: проверьте палетность товаров и объём рекомендаций."
              />
              <FboTruckDetailsDialog
                open={Boolean(selectedTruck)}
                truck={selectedTruck}
                onClose={() => setSelectedTruck(null)}
                readOnly
                editable={false}
                dndEnabled={false}
                trucks={truckPlan.trucks}
              />
            </>
          ) : viewMode === VIEW_TRUCK_LIST ? (
            <FboTruckList
              truckPlan={truckPlan}
              planKey={`${calculationMode}:${planId || 'average'}`}
              readOnly
              editable={false}
              emptyMessage="Фуры не сформированы: проверьте палетность товаров и объём рекомендаций."
            />
          ) : (
            <Stack spacing={1}>
              {selectedVisibleKeys.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={(theme) => ({ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.06) })}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="bodyMedium" fontWeight={700}>
                        Выбрано: {selectedVisibleKeys.length}
                      </Typography>
                      <Typography variant="bodySmall" color="text.secondary">
                        Позиции исключаются только из этой поставки — их можно вернуть в блоке выше.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<ExcludeIcon />}
                      disabled={!selectedVisibleKeys.length || loading}
                      onClick={excludeSelectedProducts}
                      fullWidth={isMobile}
                    >
                      Исключить из поставки
                    </Button>
                    <Button
                      disabled={!selectedVisibleKeys.length || loading}
                      onClick={() => setSelectedProductKeys([])}
                      fullWidth={isMobile}
                    >
                      Снять выбор
                    </Button>
                  </Stack>
                </Paper>
              )}
              <TableContainer component={Paper} sx={{ maxHeight: '72vh' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1092 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={selectedVisibleKeys.length > 0 && !allVisibleSelected}
                        disabled={!selectableProductKeys.length}
                        inputProps={{ 'aria-label': 'Выбрать все отображаемые товары' }}
                        onClick={(event) => event.stopPropagation()}
                        onChange={toggleAllVisibleProducts}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 44 }} />
                    <TableCell>Товар</TableCell>
                    <TableCell align="right">Спрос в день</TableCell>
                    <TableCell align="right">Прогноз</TableCell>
                    <TableCell align="right">Остаток</TableCell>
                    <TableCell align="right">Целевой запас</TableCell>
                    <TableCell align="right">К поставке</TableCell>
                    <TableCell align="right">По графику</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => {
                    const key = `${item.barcode || item.article || 'row'}_${index}`;
                    const productKey = productKeyOf(item);
                    const isOpen = Boolean(expanded[key]);
                    return (
                      <Fragment key={key}>
                        <TableRow hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={Boolean(productKey) && selectedProductKeys.includes(productKey)}
                              disabled={!productKey}
                              inputProps={{ 'aria-label': `Выбрать ${item.article || item.nm_id || 'товар'}` }}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => toggleProductSelection(productKey)}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              disabled={!item.regions?.length}
                              onClick={() => setExpanded((current) => ({ ...current, [key]: !current[key] }))}
                            >
                              {isOpen ? <DownIcon /> : <RightIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              {item.image_url ? (
                                <Box
                                  component="img"
                                  src={item.image_url}
                                  alt=""
                                  sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1 }}
                                />
                              ) : (
                                <Box sx={{ width: 44, height: 44, bgcolor: 'action.hover', borderRadius: 1 }} />
                              )}
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{item.article || 'Без названия'}</Typography>
                                <Typography variant="caption" color="text.secondary" component="div">
                                  {[item.barcode, item.tech_size, item.podgruppa].filter(Boolean).join(' · ')}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{fmtFloat(item.daily_demand)}</TableCell>
                          <TableCell align="right">{fmtNum(item.forecast_qty_horizon)}</TableCell>
                          <TableCell align="right">{fmtNum(item.total_stock)}</TableCell>
                          <TableCell align="right">
                            {editKey === key ? (
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editVal}
                                  onChange={(event) => setEditVal(event.target.value)}
                                  sx={{ width: 80 }}
                                />
                                <IconButton size="small" color="primary" onClick={() => saveTarget(item)}>
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => setEditKey(null)}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                <Tooltip title={`Применено: ${TARGET_SOURCE_LABEL[item.target_source] || 'настройка расчёта'}`}>
                                  <Typography variant="body2">{fmtFloat(item.target_days)} дн.</Typography>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  disabled={!item.barcode}
                                  aria-label="Изменить целевой запас"
                                  onClick={() => {
                                    setEditKey(key);
                                    setEditVal(String(item.target_days || ''));
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={700} color={item.total_to_supply > 0 ? 'primary.main' : 'text.secondary'}>
                              {fmtNum(item.total_to_supply)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtNum(item.schedule_total_qty)}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Исключить из поставки">
                              <IconButton
                                color="error"
                                aria-label={`Исключить ${item.article || item.nm_id} из поставки`}
                                onClick={() => excludeProduct(item)}
                                sx={{ minWidth: 48, minHeight: 48 }}
                              >
                                <ExcludeIcon />
                              </IconButton>
                            </Tooltip>
                            {isMobile && (
                              <Typography variant="caption" color="error.main" component="div">
                                Исключить
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box sx={(theme) => ({
                                m: 1,
                                p: 1,
                                overflowX: 'auto',
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                              })}>
                                <Table size="small" sx={{ minWidth: 1180 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Округ</TableCell>
                                      <TableCell align="right">Доля</TableCell>
                                      <TableCell align="right">Спрос в день</TableCell>
                                      <TableCell align="right">Остаток</TableCell>
                                      <TableCell align="right">Дней до нулевого остатка</TableCell>
                                      <TableCell>Отправить до</TableCell>
                                      <TableCell>Ожидаемое поступление</TableCell>
                                      <TableCell align="right">Срок до склада WB</TableCell>
                                      <TableCell align="right">К поставке</TableCell>
                                      <TableCell>График</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(item.regions || []).map((region) => (
                                      <TableRow key={region.okrug}>
                                        <TableCell>
                                          <Tooltip title={region.okrug}>
                                            <span>{region.okrug_short || shortMap[region.okrug] || region.okrug}</span>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">{Math.round(Number(region.region_share || 0) * 100)}%</TableCell>
                                        <TableCell align="right">{fmtFloat(region.daily_demand)}</TableCell>
                                        <TableCell align="right">{fmtNum(region.stock)}</TableCell>
                                        <TableCell align="right">{fmtFloat(region.days_to_oos)}</TableCell>
                                        <TableCell>{fmtDate(region.recommended_ship_date)}</TableCell>
                                        <TableCell>{fmtDate(region.recommended_arrival_date)}</TableCell>
                                        <TableCell align="right">{fmtNum(region.lead_days)} дн.</TableCell>
                                        <TableCell align="right">{fmtNum(region.to_supply)}</TableCell>
                                        <TableCell sx={{ minWidth: 280 }}>
                                          {renderSchedule(region.supply_schedule || [])}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              </TableContainer>
            </Stack>
          )}
        </Stack>
      )}

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Сохранить план поставок</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              autoFocus
              fullWidth
              label="Название плана"
              value={saveName}
              onChange={(event) => setSaveName(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') saveCurrentAsPlan(); }}
            />
            <Alert severity="info">
              Сохраним текущие фуры, даты, количество, фильтры и настройки. Редактировать план
              можно на странице сохранённых планов.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={saveCurrentAsPlan} disabled={saving}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
